import * as Sentry from '@sentry/react';
import { loginProviderDisplayName } from "api/auth/login_button";
import { clearToken } from "api/auth/storage";
import { Arrays } from "ext/array";
import { t } from "i18next";
import { ID } from "store/id";
import { Bcn } from "support/beacon";
import { Toast } from "toast";
import { getType } from 'typesafe-actions';
import * as actions from './actions';
export default (state, action) => {
    switch (action.type) {
        case getType(actions.addItems): {
            return Object.assign(Object.assign({}, state), { items: [...state.items, ...action.payload] });
        }
        case getType(actions.addItemsAtIndices): {
            return (() => {
                let newState = Object.assign({}, state);
                let sortedByIndex = action.payload.sort((a, b) => a.index - b.index);
                sortedByIndex.forEach(({ item, index }) => {
                    newState.items.splice(index, 0, item);
                });
                return newState;
            })();
        }
        case getType(actions.updateAppearance): {
            const { id, key, value } = action.payload;
            let newState = Object.assign({}, state);
            newState.items = state.items.map((item) => {
                if (item.id === id) {
                    let newAppearance = Object.assign({}, item.appearance);
                    newAppearance[key] = value;
                    return Object.assign(Object.assign({}, item), { appearance: newAppearance });
                }
                return item;
            });
            return newState;
        }
        case getType(actions.updateItemsAppearance): {
            const { key, idsToValuesMap } = action.payload;
            let newState = Object.assign({}, state);
            newState.items = state.items.map(item => {
                const newValue = idsToValuesMap[item.id.id];
                if (newValue !== undefined) {
                    return Object.assign(Object.assign({}, item), { appearance: Object.assign(Object.assign({}, item.appearance), { [key]: newValue }) });
                }
                return item;
            });
            return newState;
        }
        case getType(actions.updateAppearanceProperties): {
            const { id, props } = action.payload;
            let newState = Object.assign({}, state);
            newState.items = state.items.map(item => item.id === id
                ? Object.assign(Object.assign({}, item), { appearance: Object.assign(Object.assign({}, item.appearance), props) }) : item);
            return newState;
        }
        case getType(actions.updateItemsAppearanceProperties): {
            const { ids, props } = action.payload;
            let newState = Object.assign({}, state);
            newState.items = state.items.map(item => Arrays.includes(ids, item.id)
                ? Object.assign(Object.assign({}, item), { appearance: Object.assign(Object.assign({}, item.appearance), props) }) : item);
            return newState;
        }
        case getType(actions.removeItems): {
            const { ids } = action.payload;
            let newState = Object.assign({}, state);
            newState.items = state.items.filter(ID.notMatching(ids));
            return newState;
        }
        case getType(actions.addToGroup): {
            const groupsOperationData = action.payload.data;
            let newState = Object.assign({}, state);
            let existingGroupIdNumbers = [];
            newState.groups = newState.groups.map(group => {
                if (!(group.id.id in groupsOperationData)) {
                    return group;
                }
                existingGroupIdNumbers.push(group.id.id);
                const childIdsToAdd = groupsOperationData[group.id.id];
                group.items.push(...childIdsToAdd);
                return group;
            });
            // Add any new groups.
            for (let groupIdNumber in groupsOperationData) {
                let groupIdNumberAsNumber = +groupIdNumber;
                if (existingGroupIdNumbers.indexOf(groupIdNumberAsNumber) === -1) {
                    // Group does not exist yet.
                    newState.groups.push({
                        id: action.payload.groupIds[groupIdNumber],
                        items: groupsOperationData[groupIdNumber]
                    });
                }
            }
            return newState;
        }
        case getType(actions.removeFromGroup): {
            const groupsOperationData = action.payload.data;
            let newState = Object.assign({}, state);
            let newGroups = [];
            for (const group of newState.groups) {
                if (group.id.id in groupsOperationData) {
                    const childIdsTorRemove = groupsOperationData[group.id.id];
                    group.items = group.items.filter(i => childIdsTorRemove.indexOf(i) === -1);
                    // Only keep this group if it still has any children.
                    if (group.items.length > 0) {
                        newGroups.push(group);
                    }
                }
                else {
                    // This group is not concerned. Keep it.
                    newGroups.push(group);
                }
            }
            newState.groups = newGroups;
            return newState;
        }
        case getType(actions.setItemsRotation): {
            const { rotations } = action.payload;
            let newState = Object.assign({}, state);
            newState.items = state.items.map((item) => {
                if (item.id.id in rotations) {
                    return Object.assign(Object.assign({}, item), { rotation: rotations[item.id.id] });
                }
                return item;
            });
            return newState;
        }
        case getType(actions.switchTool): {
            let toolName = action.payload;
            return Object.assign(Object.assign({}, state), { tool: toolName });
        }
        case getType(actions.setThemeStrokeThickness): {
            return Object.assign(Object.assign({}, state), { theme: Object.assign(Object.assign({}, state.theme), { strokeThickness: action.payload }) });
        }
        case getType(actions.updateChemMarkConfigStateOfMatterStyle): {
            return Object.assign(Object.assign({}, state), { chemMark: Object.assign(Object.assign({}, state.chemMark), { stateOfMatterStyle: action.payload }) });
        }
        case getType(actions.generic): {
            let actionFn = action.payload.actionFn;
            let newState = actionFn(state);
            return newState;
        }
        case getType(actions.logIn): {
            Bcn.setSessionData(action.payload.provider, action.payload.user, action.payload.tier);
            Sentry.setUser({ id: action.payload.user.id, email: action.payload.user.email });
            if (!(action.payload.silencePopup === true)) {
                let title = t(action.payload.firstTimeLogin ? "login.sign_in_success_first" : "login.sign_in_success");
                let description = t('login.current_login', { provider: t(loginProviderDisplayName(action.payload.provider)), handle: action.payload.user.handle });
                toastr.success(description, title);
            }
            return Object.assign(Object.assign({}, state), { auth: {
                    state: "logged_in",
                    provider: action.payload.provider,
                    jwtToken: action.payload.jwtToken,
                    user: action.payload.user,
                }, tier: action.payload.tier });
        }
        case getType(actions.logOut): {
            Bcn.unsetSessionData();
            Sentry.setUser(null);
            Toast.info(t('login.sign_out_description'), t('login.sign_out_title'));
            clearToken();
            return Object.assign(Object.assign({}, state), { auth: {
                    state: "logged_out",
                }, tier: undefined });
        }
        case getType(actions.eraseItems): {
            return Object.assign(Object.assign({}, state), { items: [], arrows: [], groups: [] });
        }
        case getType(actions.replace): {
            // Merge current state with the one in the given document.
            // If the diagram is a template, do not set up information
            // about current diagram.
            const { diagram, isTemplate } = action.payload;
            return Object.assign(Object.assign(Object.assign({}, state), diagram.data), { currentCloudDiagram: !isTemplate
                    ? {
                        id: diagram.id,
                        title: diagram.title,
                    }
                    : undefined });
        }
        case getType(actions.setCloudDiagram): {
            return Object.assign(Object.assign({}, state), { currentCloudDiagram: action.payload });
        }
        case getType(actions._TESTONLY_resetState): {
            // Override in tests only.
            return state;
        }
        default: return state; // Needed for initial store state.
    }
};
